/**
 * Spain / Thailand province map: one marker per GTG province. Tap opens detail modal → Add to list (Kenya / US ZCTA pattern).
 */
import React, { useCallback, useMemo, useState } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { getMapTilerStreetTileUrl } from "../../utils/mapTilerStreetTileUrl";
import { Box, Typography } from "@mui/material";
import {
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
  normalizeGtgTargetCountry,
  getGtgCanonicalSubdivisionList,
} from "@healthdesk/shared-utils";
import "leaflet/dist/leaflet.css";
import GtgProvinceMapMarker from "./GtgProvinceMapMarker";
import GtgProvinceDetailModal from "./GtgProvinceDetailModal";
import {
  getGtgProvinceCentroid,
  getGtgProvinceDisplayName,
  SPAIN_PROVINCE_GEO_SOURCE_NOTE,
  THAILAND_PROVINCE_GEO_SOURCE_NOTE,
  GTG_PROVINCE_GOOGLE_ADS_NOTE,
} from "../../services/census/getGtgProvinceMapContext";

const SPAIN_MAP = { center: { lat: 40.2, lng: -3.7 }, zoom: 6 };
const THAILAND_MAP = { center: { lat: 15.5, lng: 101.2 }, zoom: 6 };

const GtgProvinceMapPicker = ({
  targetCountry,
  selectedProvinces,
  onProvinceToggle,
  disabled = false,
  height = 380,
}) => {
  const tc = normalizeGtgTargetCountry(targetCountry);
  const [detailProvince, setDetailProvince] = useState(null);

  const handleOpenDetail = useCallback((key) => {
    setDetailProvince(typeof key === "string" ? key : "");
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailProvince(null);
  }, []);

  const provinceList = useMemo(() => {
    if (tc !== GTG_TARGET_COUNTRY_ES && tc !== GTG_TARGET_COUNTRY_TH) return [];
    return [...getGtgCanonicalSubdivisionList(tc)];
  }, [tc]);

  const markers = useMemo(
    () =>
      provinceList
        .map((canon) => {
          const pos = getGtgProvinceCentroid(tc, canon);
          if (!pos) return null;
          return {
            province: canon,
            displayName: getGtgProvinceDisplayName(canon),
            lat: pos.lat,
            lng: pos.lng,
          };
        })
        .filter(Boolean),
    [provinceList, tc]
  );

  const mapCfg = tc === GTG_TARGET_COUNTRY_TH ? THAILAND_MAP : SPAIN_MAP;
  const geoNote = tc === GTG_TARGET_COUNTRY_TH ? THAILAND_PROVINCE_GEO_SOURCE_NOTE : SPAIN_PROVINCE_GEO_SOURCE_NOTE;

  if (tc !== GTG_TARGET_COUNTRY_ES && tc !== GTG_TARGET_COUNTRY_TH) {
    return null;
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        <strong>Tap a marker</strong> to open details and use <strong>Add to list</strong> (two steps, same as Kenya /
        US). {GTG_PROVINCE_GOOGLE_ADS_NOTE} {geoNote}
      </Typography>
      <Box sx={{ height, width: "100%", position: "relative" }}>
        <MapContainer
          center={[mapCfg.center.lat, mapCfg.center.lng]}
          zoom={mapCfg.zoom}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            url={getMapTilerStreetTileUrl()}
            attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
          />
          {markers.map((m) => (
            <GtgProvinceMapMarker
              key={m.province}
              provinceKey={m.province}
              displayName={m.displayName}
              lat={m.lat}
              lng={m.lng}
              isSelected={selectedProvinces.has(m.province)}
              onOpenDetail={handleOpenDetail}
              disabled={disabled}
            />
          ))}
        </MapContainer>
      </Box>

      <GtgProvinceDetailModal
        open={Boolean(detailProvince)}
        targetCountry={tc}
        province={detailProvince}
        isSelected={detailProvince ? selectedProvinces.has(detailProvince) : false}
        onClose={handleCloseDetail}
        onAddOrRemove={onProvinceToggle}
      />
    </Box>
  );
};

export default GtgProvinceMapPicker;
